import { SharedAppConstants } from '@tock/shared-app-constants';
import { computeSavedEntityState } from 'state/utils/computeSavedEntityState';
import { core, payloadToJSON } from '@tock/coretypes';
import { dashboard } from 'types';
import { find, forEach } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';
import { todayBusinessDay } from '@tock/shared/utils/time';
import AppConstants from 'constants/AppConstants';

export interface GuestData {
  patronId: number;
  guestProfile: dashboard.GuestProfile;
  businessGuestProfile: dashboard.BusinessGuestProfile;
}

interface GuestDataState {
  guestData: NumericDictionary<GuestData>;
}

interface SavedState {
  [date: string]: GuestDataState;
}

interface State {
  guestData: NumericDictionary<GuestData>;
  savedState: SavedState;
}

export const initialState: State = {
  guestData: {},
  savedState: {},
};

function updateGuestProfile(
  guestData: NumericDictionary<GuestData>,
  businessId: number,
  guestProfile?: dashboard.GuestProfile
) {
  const patronId = guestProfile?.patron?.id;
  if (!patronId) {
    return;
  }
  const currentGuestDataForPatronId = guestData[patronId] ?? {};
  const updatedGuestProfile = {
    ...currentGuestDataForPatronId.guestProfile,
    ...guestProfile,
  };
  const updateIncludesBusinessGuestProfiles = !!guestProfile?.businessGuestProfile.length;
  const currentBusinessGuestProfile =
    currentGuestDataForPatronId.guestProfile?.businessGuestProfile ?? [];

  updatedGuestProfile.businessGuestProfile = updateIncludesBusinessGuestProfiles
    ? guestProfile?.businessGuestProfile.map((bGProfile) => {
        const matchBusinessGuestProfile = currentBusinessGuestProfile.find(
          (cBGPprofile) => cBGPprofile.business?.id === bGProfile.business?.id
        );
        return {
          ...matchBusinessGuestProfile,
          ...bGProfile,
        };
      })
    : currentBusinessGuestProfile.filter((existing) => existing.business?.id === businessId);
  guestData[patronId] = {
    ...currentGuestDataForPatronId,
    patronId,
    guestProfile: updatedGuestProfile,
    businessGuestProfile: find(
      updatedGuestProfile.businessGuestProfile,
      (profile) => profile.business?.id === businessId
    ) as dashboard.BusinessGuestProfile,
  };
}

function updateGuestData(
  update: dashboard.ICalendarDayEntityUpdate,
  guestData: NumericDictionary<GuestData>,
  businessId: number
) {
  forEach(update.ticketGroup as core.TicketGroup[], (ticketGroup) => {
    forEach(ticketGroup.purchaseDetail, (purchase) => {
      updateGuestProfile(
        guestData,
        businessId,
        oc(purchase).dinerGuestProfile() as dashboard.GuestProfile
      );
    });
  });
  forEach(update.walkin as dashboard.WalkIn[], (walkIn) => {
    updateGuestProfile(
      guestData,
      businessId,
      oc(walkIn).dinerGuestProfile() as dashboard.GuestProfile
    );
  });
  forEach(update.purchase as core.PastPurchase[], (purchase) => {
    updateGuestProfile(
      guestData,
      businessId,
      oc(purchase).dinerGuestProfile() as dashboard.GuestProfile
    );
  });
}

export default function reducer(state: State = initialState, action: BaseTockAction): State {
  const { type, payload, context } = action;
  switch (type) {
    case AppConstants.CALENDAR_ENTITY_UPDATE: {
      const changeUpdates = payload as dashboard.CalendarDayEntityUpdates;
      if (oc(changeUpdates).update[0].type() === 'INIT') {
        const guestData: NumericDictionary<GuestData> = {};
        const update = oc(changeUpdates).update[0]();
        if (!update) {
          return {
            ...state,
            guestData: {},
          };
        }
        updateGuestData(update, guestData, context);
        return {
          ...state,
          guestData,
        };
      } else {
        const guestData = state.guestData;
        forEach(changeUpdates.update, (update) => {
          if (update.type === 'REPLACE') {
            updateGuestData(update, guestData, context);
          }
        });

        return {
          ...state,
          guestData: {
            ...guestData,
          },
        };
      }
    }
    case SharedAppConstants.GUEST_PROFILE_GET_DONE: {
      if (!context) {
        return state;
      }
      const guestData = { ...state.guestData };
      const guestProfile = payloadToJSON<dashboard.GuestProfile>(payload);
      updateGuestProfile(guestData, context, guestProfile);
      return { ...state, guestData };
    }
    case AppConstants.CALENDAR_ENTITY_SAVE_DATE: {
      const stateToSave: GuestDataState = {
        guestData: state.guestData,
      };
      const newSavedState: SavedState = computeSavedEntityState<GuestDataState>({
        newDate: payload,
        stateToSave,
        savedState: state.savedState,
        todayString: todayBusinessDay().toString(),
      });

      return {
        ...state,
        savedState: newSavedState,
      };
    }
    case AppConstants.CALENDAR_ENTITY_RESTORE_DATE: {
      if (!state.savedState) {
        return state;
      }
      const restoredState = state.savedState[payload];
      if (!restoredState) {
        return {
          ...state,
          guestData: initialState.guestData,
        };
      }
      return {
        ...state,
        guestData: restoredState.guestData,
      };
    }
    default:
      return state;
  }
}
